const HOUR_MS = 1000 * 60 * 60;
const DAY_MS = HOUR_MS * 24;
const YEAR_MS = DAY_MS * 365.25;

/**
 * Localised "Jan", "Feb", … via `Intl.DateTimeFormat` rather than a
 * hard-coded array. Cached because constructing a formatter on every
 * tick would be wasteful, and centralised so swapping to a runtime
 * locale (e.g. `i18next.language`) later is a one-line change.
 */
const monthShortFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  timeZone: 'UTC',
});

const formatMonthShort = (d: Date): string => monthShortFormatter.format(d);

const pad2 = (n: number) => n.toString().padStart(2, '0');

export interface XAxisTick {
  ms: number;
  label: string;
  highlight?: boolean;
}

type TickUnit =
  | { kind: 'hour'; stepHours: number }
  | { kind: 'day'; stepDays: number }
  | { kind: 'month'; stepMonths: number };

const pickUnit = (spanMs: number): TickUnit => {
  const days = spanMs / DAY_MS;
  const years = spanMs / YEAR_MS;

  if (spanMs <= 2 * DAY_MS) {
    return { kind: 'hour', stepHours: 1 };
  }

  if (days <= 5) {
    return { kind: 'hour', stepHours: 4 };
  }

  if (days <= 21) {
    return { kind: 'day', stepDays: 1 };
  }

  if (days < 42) {
    return { kind: 'day', stepDays: 7 };
  }

  if (days <= 370) {
    return { kind: 'month', stepMonths: 1 };
  }

  if (years <= 3) {
    return { kind: 'month', stepMonths: 3 };
  }

  if (years <= 5) {
    return { kind: 'month', stepMonths: 4 };
  }

  if (years <= 10) {
    return { kind: 'month', stepMonths: 6 };
  }

  if (years <= 15) {
    return { kind: 'month', stepMonths: 12 };
  }

  if (years <= 30) {
    return { kind: 'month', stepMonths: 24 };
  }

  if (years <= 45) {
    return { kind: 'month', stepMonths: 36 };
  }

  if (years <= 65) {
    return { kind: 'month', stepMonths: 48 };
  }

  if (years <= 85) {
    return { kind: 'month', stepMonths: 60 };
  }

  return { kind: 'month', stepMonths: 120 };
};

const floorToHour = (ms: number, stepHours: number): number => {
  const d = new Date(ms);
  const h = Math.floor(d.getUTCHours() / stepHours) * stepHours;

  return Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    h,
    0,
    0,
    0
  );
};

const floorToDay = (ms: number): number => {
  const d = new Date(ms);

  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

const floorToMonday = (ms: number): number => {
  const d = new Date(ms);
  const dayOfWeek = d.getUTCDay(); // 0 Sun .. 6 Sat
  const daysFromMonday = (dayOfWeek + 6) % 7;

  return Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate() - daysFromMonday
  );
};

const floorToMonth = (ms: number, stepMonths: number): number => {
  const d = new Date(ms);

  if (stepMonths >= 12) {
    const yearStep = Math.floor(stepMonths / 12);
    const y = Math.floor(d.getUTCFullYear() / yearStep) * yearStep;

    return Date.UTC(y, 0, 1);
  }

  const m = Math.floor(d.getUTCMonth() / stepMonths) * stepMonths;

  return Date.UTC(d.getUTCFullYear(), m, 1);
};

const advance = (ms: number, unit: TickUnit): number => {
  const d = new Date(ms);

  if (unit.kind === 'hour') {
    return ms + unit.stepHours * HOUR_MS;
  }

  if (unit.kind === 'day') {
    return Date.UTC(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate() + unit.stepDays
    );
  }

  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + unit.stepMonths, 1);
};

const firstTick = (domainStart: number, unit: TickUnit): number => {
  if (unit.kind === 'hour') {
    return floorToHour(domainStart, unit.stepHours);
  }

  if (unit.kind === 'day') {
    return unit.stepDays === 7
      ? floorToMonday(domainStart)
      : floorToDay(domainStart);
  }

  return floorToMonth(domainStart, unit.stepMonths);
};

const labelFor = (
  d: Date,
  prev: Date | null,
  unit: TickUnit
): { label: string; highlight: boolean } => {
  if (unit.kind === 'hour') {
    const dayChanged = !prev || prev.getUTCDate() !== d.getUTCDate();

    if (dayChanged) {
      return {
        label: `${formatMonthShort(d)} ${d.getUTCDate()}`,
        highlight: true,
      };
    }

    return {
      label: `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`,
      highlight: false,
    };
  }

  if (unit.kind === 'day') {
    const yearChanged = !!prev && prev.getUTCFullYear() !== d.getUTCFullYear();
    const monthChanged = !!prev && prev.getUTCMonth() !== d.getUTCMonth();

    if (yearChanged) {
      return { label: String(d.getUTCFullYear()), highlight: true };
    }

    if (monthChanged) {
      return { label: formatMonthShort(d), highlight: true };
    }

    return { label: String(d.getUTCDate()), highlight: false };
  }

  if (unit.stepMonths >= 12) {
    return { label: String(d.getUTCFullYear()), highlight: false };
  }

  const yearChanged = !prev || prev.getUTCFullYear() !== d.getUTCFullYear();

  if (yearChanged) {
    return { label: String(d.getUTCFullYear()), highlight: true };
  }

  return { label: formatMonthShort(d), highlight: false };
};

export const generateXAxisTicks = (domain: [number, number]): XAxisTick[] => {
  const span = domain[1] - domain[0];

  if (!Number.isFinite(span) || span <= 0) {
    return [];
  }

  const unit = pickUnit(span);
  const ticks: XAxisTick[] = [];

  let current = firstTick(domain[0], unit);

  while (current < domain[0]) {
    current = advance(current, unit);
  }

  let prev: Date | null = null;

  while (current <= domain[1]) {
    const d = new Date(current);
    const { label, highlight } = labelFor(d, prev, unit);

    ticks.push({ ms: current, label, highlight });
    prev = d;
    current = advance(current, unit);
  }

  return ticks;
};
